import { useEffect, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaStore } from "react-icons/fa6";
import { Alert, Snackbar } from "@mui/material";
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Row, Space, Typography, Spin } from "antd";
import useStore from "../../../../hooks/useStore";
import { postProvisionStore, postDeprovisionStore } from "../../../apiRepository/Ubereats";
import getStoreDetails from "../../../apiRepository/getStoreDetails";

export async function loader({ params }) {
  const ubereatsProvisionType = sessionStorage.getItem("ubereatsProvisionType");
  const ubereatsAccessToken = sessionStorage.getItem("ubereatsAccessToken");
  const ubereatsStores = JSON.parse(sessionStorage.getItem("ubereatsStores"));
  const integratorId = sessionStorage.getItem("integratorId");
  const storeId = sessionStorage.getItem("storeId");
  const token = sessionStorage.getItem("token");

  if (!ubereatsProvisionType || !ubereatsAccessToken) {
    throw new Response("", {
      status: 404,
      statusText: "Page not found.",
    });
  }

  return {
    ubereatsAccessToken,
    ubereatsProvisionType,
    ubereatsStores,
    integratorId,
    storeId,
    action: params?.action,
    token
  };
}

export default function StoreProvision() {
  const navigate = useNavigate();
  const { ubereatsAccessToken, ubereatsStores, integratorId, storeId, action, token } = useLoaderData();
  const { setStore } = useStore();
  const [isPageLoading, setPageIsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [snackbar, setSnackbar] = useState({
    severity: "error",
    message: "",
    open: false,
  });

  useEffect(() => {
    const getStore = async () => {
      try {
        const storeDetails = await getStoreDetails({
          integratorId,
          storeId,
          token,
        });

        setStore((prev) => ({ ...prev, ...storeDetails, ubereatsAccessToken }));

        if (storeDetails.deliverySettings?.uberEats) {
          setSelectedStoreId(storeDetails.deliverySettings.uberEats.storeId)
        }
      } catch (err) {
        console.error(err?.response || err);
        console.error(JSON.stringify(err));
      } finally {
        setPageIsLoading(false);
      }
    };

    getStore();
  }, [storeId, integratorId, setStore, ubereatsAccessToken]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleProvision = async (uberEatsStoreId) => {
    setIsLoading(true);
    setSelectedStoreId(uberEatsStoreId);
    try {
      if (action === "provision") {
        await postProvisionStore({
          integratorId,
          storeId,
          accessToken: ubereatsAccessToken,
          uberStoreId: uberEatsStoreId,
          token
        });
      } else {
        await postDeprovisionStore({
          storeId,
          token
        });
      }

      setSnackbar({
        severity: "success",
        message:
          action === "provision" ? "Onboarding Success" : "Deprovision Success",
        open: true,
        onClose: () => navigate("/pos/delivery-services/ubereats"),
      });
    } catch (err) {
      const errors = err?.response?.data?.errors || [];
      setSnackbar({
        severity: "error",
        message: errors.map((error, i) => <p key={i}>{error}</p>),
        open: true,
        onClose: handleCloseSnackbar,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isPageLoading)
    return (
      <Flex
        justify="center"
        align="center"
        style={{ height: '100vh' }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} />
      </Flex>
    );

  return (
    <>
      <Row
        style={{
          minHeight: "100vh",
          minWidth: "100vw",
          backgroundColor: "#f1f5f9",
        }}
      >
        <Col xs={{ span: 22, offset: 1 }} xl={{ span: 18, offset: 3 }}>
          <Flex vertical style={{ height: "100%" }}>
            <Row
              style={{
                backgroundColor: "#E2E8F0",
                padding: "0.5rem 0.5rem 1rem 0.5rem",
              }}
              justify="space-between"
            >
              <Col span={24}>
                <div className="p-4">
                  <Typography.Title level={4}>
                    SELECT UBEREATS STORE
                  </Typography.Title>

                  {!ubereatsStores ||
                    (ubereatsStores?.length <= 0 && (
                      <Typography.Text strong>No record found.</Typography.Text>
                    ))}

                  <Space direction="vertical" style={{ width: "100%" }}>
                    {(ubereatsStores || []).map((store) => (
                      <div
                        key={store.id}
                        style={{
                          background: "#f5f5f5",
                          padding: "16px",
                          borderRadius: "4px",
                        }}
                      >
                        <Flex justify="space-between" align="center">
                          <div>
                            <Typography.Text
                              strong
                              style={{ fontSize: "16px" }}
                            >
                              <FaStore
                                style={{ color: "#8c8c8c", marginRight: 8 }}
                              />
                              {store?.name}
                            </Typography.Text>
                            <br />
                            <Typography.Text>
                              <FaMapMarkerAlt
                                style={{ color: "#8c8c8c", marginRight: 8 }}
                              />
                              {store?.location?.street_address_line_one}
                            </Typography.Text>
                          </div>
                          <Button
                            type="primary"
                            size="large"
                            loading={isLoading && store.id === selectedStoreId}
                            disabled={selectedStoreId && selectedStoreId !== store.id}
                            onClick={() => handleProvision(store.id)}
                            {...(action === "deprovision"
                              ? { danger: true }
                              : selectedStoreId && selectedStoreId !== store.id
                                ? {}
                                : { style: { background: "green" } }
                            )}
                          >
                            {action === "provision" ? "CONNECT" : "DISCONNECT"}
                          </Button>
                        </Flex>
                      </div>
                    ))}
                  </Space>
                </div>
              </Col>
            </Row>
          </Flex>
        </Col>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={snackbar.onClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            variant="filled"
            onClose={snackbar.onClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Row>
    </>
  );
}
